import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { listExamsPage, getExamsSummary } from '@/api/exams'
import { useAuth } from '@/context/AuthContext'
import GuestModal          from '@/components/exam/GuestModal'
import ExamInfoModal       from '@/components/exam/ExamInfoModal'
import { SectionHead, Scroller } from '@/components/aurora'
import SubjectTile         from '@/components/exam/SubjectTile'
import PersonalResultCard  from '@/components/exam/PersonalResultCard'
import ExamFilters         from '@/components/exam/ExamFilters'
import ExamCard            from '@/components/exam/ExamCard'
import { FileText } from 'lucide-react'

/** UI chip key → the `?status=` value the API understands. */
const FILTER_TO_STATUS = {
  all: 'all',
  open: 'available',
  cert: 'cert',
  upcoming: 'upcoming',
  expired: 'expired',
}

function status(exam) {
  const now = Date.now()
  if (exam.start_time && new Date(exam.start_time).getTime() > now) return 'locked'
  if (exam.end_time   && new Date(exam.end_time).getTime()   < now) return 'expired'
  return 'open'
}

function isNewExam(exam, daysWindow = 7) {
  if (!exam.created_at) return false
  return Date.now() - new Date(exam.created_at).getTime() < daysWindow * 86_400_000
}

export default function PublicExamsPage() {
  const { t }      = useTranslation()
  const navigate   = useNavigate()
  const { isAuth } = useAuth()

  const [exams, setExams]           = useState([])
  const [page, setPage]             = useState(1)
  const [hasMore, setHasMore]       = useState(false)
  const [loading, setLoading]       = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError]           = useState(null)
  const [filter, setFilter]         = useState('all')
  const [subject, setSubject]       = useState(null)
  const [summary, setSummary]       = useState({ counts: {}, subjects: [] })
  const [personalResults, setPersonalResults] = useState([])
  const [guestTarget, setGuestTarget] = useState(null)
  // The exam whose info modal is open. Tapping "Boshlash" opens this; the
  // modal's own start button is what actually begins the (timed) session.
  const [infoExam, setInfoExam] = useState(null)

  // Bumped on every filter/subject change so a slow in-flight page can't
  // append its rows on top of a list that has since been reset.
  const requestIdRef = useRef(0)
  const summaryRequestIdRef = useRef(0)
  const sentinelRef  = useRef(null)

  const apiStatus = FILTER_TO_STATUS[filter]

  // ── First page: reset and refetch whenever the filter or subject changes ──
  useEffect(() => {
    const id = ++requestIdRef.current
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    listExamsPage({ page: 1, status: apiStatus, subject, signal: controller.signal })
      .then(({ results, hasMore: more }) => {
        if (id !== requestIdRef.current) return
        setExams(results)
        setPage(1)
        setHasMore(more)
      })
      .catch((err) => {
        if (id === requestIdRef.current && err.code !== 'ERR_CANCELED') setError(err.message)
      })
      .finally(() => {
        if (id === requestIdRef.current) setLoading(false)
      })

    return () => controller.abort()
  }, [apiStatus, subject])

  // ── Next page, triggered by the sentinel scrolling into view ──
  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return
    const id = requestIdRef.current
    setLoadingMore(true)

    listExamsPage({ page: page + 1, status: apiStatus, subject })
      .then(({ results, hasMore: more }) => {
        if (id !== requestIdRef.current) return
        setExams((prev) => [...prev, ...results])
        setPage((p) => p + 1)
        setHasMore(more)
      })
      .catch((err) => {
        if (id === requestIdRef.current) setError(err.message)
      })
      .finally(() => {
        if (id === requestIdRef.current) setLoadingMore(false)
      })
  }, [loading, loadingMore, hasMore, page, apiStatus, subject])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    // rootMargin gives the next page a head start so the list rarely stalls
    // visibly at the bottom.
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore() },
      { rootMargin: '400px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  // ── Counts + subject buckets: aggregated server-side over the whole set,
  //    so they stay correct no matter how many pages are loaded. ──
  const refreshSummary = useCallback((signal) => {
    const id = ++summaryRequestIdRef.current
    getExamsSummary({ subject, signal })
      .then((data) => { if (id === summaryRequestIdRef.current) setSummary(data) })
      .catch((err) => {
        if (err.code !== 'ERR_CANCELED') { /* chips simply render without counts */ }
      })
  }, [subject])

  // Owns its own AbortController so StrictMode's mount→cleanup→mount in dev
  // (and any real subject-change race) cancels the stale request instead of
  // letting two calls land.
  useEffect(() => {
    const controller = new AbortController()
    refreshSummary(controller.signal)
    return () => controller.abort()
  }, [refreshSummary])

  useEffect(() => {
    if (!isAuth) { setPersonalResults([]); return }
    let cancelled = false
    listExamsPage({ page: 1, status: 'mine', subject })
      .then(({ results }) => { if (!cancelled) setPersonalResults(results) })
      .catch(() => { if (!cancelled) setPersonalResults([]) })
    return () => { cancelled = true }
  }, [isAuth, subject])

  // A long-idle tab holds a stale snapshot. Refresh the (cheap) summary on
  // focus rather than the list — resetting the list would discard every page
  // the reader has already scrolled through.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') refreshSummary()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refreshSummary])

  const counts = useMemo(() => {
    const c = summary.counts ?? {}
    return { all: c.all, open: c.available, cert: c.cert, upcoming: c.upcoming, expired: c.expired, mine: c.mine }
  }, [summary])

  // Card CTA → show the exam-info modal first (does NOT start the session).
  function handleStart(exam) {
    if (status(exam) !== 'open') return
    setInfoExam(exam)
  }

  // The info modal's "Boshlash" → actually begin (guest check, then navigate).
  function handleBegin() {
    const exam = infoExam
    if (!exam) return
    setInfoExam(null)
    if (!isAuth) { setGuestTarget(exam.code); return }
    navigate(`/exam/${exam.code}`)
  }

  const subjects = summary.subjects ?? []

  return (
    <div>
      <div className="w-full px-4 sm:px-8 pb-12 pt-5">
        {/* Tagline */}
        <p className="text-[14.5px] mb-4" style={{ color: 'var(--ink-2)' }}>
          {t('exams.tagline1', 'Onlayn imtihonlar')} —{' '}
          <em className="not-italic font-semibold" style={{ color: 'var(--coral)' }}>
            {t('exams.tagline2', 'bilim, sertifikat, reyting.')}
          </em>
        </p>

        {/* Error */}
        {error && (
          <div className="px-4 py-3 my-3 rounded-xl text-sm" style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        {/* Personal results */}
        {isAuth && personalResults.length > 0 && (
          <section className="mb-5">
            <SectionHead title={t('exams.continueTitle', 'Davom etishingiz mumkin')} count={counts.mine ?? personalResults.length} />
            <Scroller>
              {personalResults.map((e) => (
                <PersonalResultCard
                  key={e.id} exam={e}
                  onResult={() => navigate(`/exam/${e.code}/result`)}
                  onRetake={() => handleStart(e)}
                />
              ))}
            </Scroller>
          </section>
        )}

        {/* Subject tiles — tap to narrow the list, tap again to clear */}
        {subjects.length > 0 && (
          <section className="mb-5">
            <SectionHead title={t('exams.subjects', 'Fanlar')} count={subjects.length} />
            <Scroller>
              {subjects.map(({ subject: subj, exam_count, cert_count }) => (
                <SubjectTile
                  key={subj.id} subject={subj}
                  examCount={exam_count} certCount={cert_count}
                  onClick={() => setSubject((cur) => (cur === subj.id ? null : subj.id))}
                />
              ))}
            </Scroller>
          </section>
        )}

        {subject != null && (
          <button
            onClick={() => setSubject(null)}
            className="mb-3 text-[13px] font-medium"
            style={{ border: 0, background: 'transparent', color: 'var(--coral)', cursor: 'pointer' }}
          >
            {t('exams.clearSubject', "Fan filtrini tozalash")}
          </button>
        )}

        {/* Filters */}
        <div className="mb-4">
          <ExamFilters active={filter} onChange={setFilter} counts={counts} />
        </div>

        {/* Initial loader */}
        {loading && (
          <div className="flex justify-center py-16">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }}
            />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && exams.length === 0 && (
          <div
            className="text-center py-14 rounded-2xl border border-dashed"
            style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}
          >
            <FileText size={36} strokeWidth={1.5} className="mx-auto mb-3" style={{ color: 'var(--ink-3)' }} />
            <p className="text-sm m-0" style={{ color: 'var(--ink-3)' }}>
              {filter === 'all'
                ? t('exams.empty', "Hozir ochiq imtihonlar yo'q.")
                : t('exams.emptyFiltered', "Bu filterga mos imtihon topilmadi.")}
              {filter !== 'all' && (
                <button
                  onClick={() => setFilter('all')}
                  className="ml-1 font-medium"
                  style={{ border: 0, background: 'transparent', color: 'var(--coral)', cursor: 'pointer' }}
                >
                  {t('exams.showAll', "Hammasini ko'rsatish")}
                </button>
              )}
            </p>
          </div>
        )}

        {/* Exam list */}
        {!loading && (
          <div className="flex flex-col gap-2.5">
            {exams.map((exam) => (
              <ExamCard
                key={exam.id} exam={exam}
                isNew={isNewExam(exam)}
                onStart={() => handleStart(exam)}
                onResult={() => navigate(`/exam/${exam.code}/result`)}
                onLeaderboard={() => navigate(`/exam/${exam.code}/leaderboard`)}
              />
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel */}
        <div ref={sentinelRef} aria-hidden="true" className="h-px" />

        {loadingMore && (
          <div className="flex justify-center py-6">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }}
            />
          </div>
        )}
      </div>

      {infoExam && (
        <ExamInfoModal
          exam={infoExam}
          onClose={() => setInfoExam(null)}
          onStart={handleBegin}
        />
      )}

      {guestTarget != null && (
        <GuestModal
          onClose={() => setGuestTarget(null)}
          onSuccess={() => { const id = guestTarget; setGuestTarget(null); navigate(`/exam/${id}`) }}
        />
      )}
    </div>
  )
}
